import { useId } from 'react';
import type { PorRegimeDto, PorRegimeEtapaResponse } from '@triade/contracts';
import { usePorRegime } from '@/api/precificacao';
import { REGIME_ROTULO, SeloEstimada } from '@/components/reforma/reforma-ui';
import { formatarData, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';

export type Manter = PorRegimeDto['manter'];

const ROTULO_MANTER: Record<Manter, string> = { REAIS: 'R$ (padrão)', PERCENTUAIS: 'percentuais' };
const TEXTO_MANTER =
  'Em R$, a despesa fixa e o lucro por unidade do preço de hoje ficam iguais em todos os anos e só mudam os tributos e as despesas variáveis percentuais. Em percentuais, a despesa fixa e o lucro acompanham o preço (e caem quando os tributos caem).';

/**
 * Painel "Preço por regime": o mesmo item/tabela/condicao (e praticado) em cada etapa da reforma. Valores da API.
 * `embutido`: usado dentro do bloco "Reforma tributária" (sem cartao proprio, subtitulo em vez de titulo).
 * `onMudarManter`: o seletor "Manter" (R$ ou percentuais) e controlado pelo pai, que monta o `dto` com `manter`.
 */
export function PainelPorRegime({
  dto,
  embutido = false,
  onSelecionar,
  selecionada = null,
  onMudarManter,
}: {
  dto: PorRegimeDto | null;
  embutido?: boolean;
  /** Clicar numa linha carrega a configuracao daquele ano no topo do bloco (S36). Sem isto as linhas nao sao clicaveis. */
  onSelecionar?: (etapa: PorRegimeEtapaResponse) => void;
  /** Chave (`etapa-data`) da linha carregada no topo. */
  selecionada?: string | null;
  /** Troca o "Manter" (R$ ou percentuais); o pai refaz a consulta com o `dto` novo. */
  onMudarManter: (manter: Manter) => void;
}) {
  const consulta = usePorRegime(dto);
  const comPraticado = dto?.praticado !== undefined;
  const manter = dto?.manter ?? 'REAIS';
  const colunas = comPraticado ? 10 : 7;
  const idSeletor = useId();

  const cabecalho = (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      {embutido ? (
        <h3 className="text-small font-semibold text-ink-body">Preço por regime</h3>
      ) : (
        <h2 className="text-title font-semibold">Preço por regime</h2>
      )}
      {consulta.isFetching && <span className="text-caption text-ink-muted">Calculando…</span>}
    </div>
  );

  const seletor = (
    <div className="flex flex-col gap-1">
      <div role="radiogroup" aria-labelledby={`${idSeletor}-rotulo`} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-small">
        <span id={`${idSeletor}-rotulo`} className="text-caption font-medium text-ink-body">Manter:</span>
        {(['REAIS', 'PERCENTUAIS'] as const).map((m) => (
          <label key={m} className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name={idSeletor}
              value={m}
              checked={manter === m}
              onChange={() => onMudarManter(m)}
              className="size-4 accent-primary"
            />
            {ROTULO_MANTER[m]}
          </label>
        ))}
      </div>
      <p className="text-caption text-ink-muted">{TEXTO_MANTER}</p>
    </div>
  );

  const corpo = (
    <>
      {consulta.isError && (
        <p role="alert" className="text-small text-danger-ink">
          {consulta.error.message}
        </p>
      )}

      {consulta.data && (
        <>
          <p data-testid="por-regime-manter" className="text-caption text-ink-muted">
            Projeção mantida em {consulta.data.manter === 'REAIS' ? 'R$' : 'percentuais'}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-small">
              <thead className="text-caption text-ink-muted">
                <tr>
                  <th scope="col" className="py-1.5 pr-3 font-medium">Etapa</th>
                  <th scope="col" className="py-1.5 pr-3 font-medium">Regime</th>
                  <th scope="col" className="py-1.5 pr-3 font-medium">A partir de</th>
                  <th scope="col" className="py-1.5 pr-3 text-right font-medium">Preço sem tributos</th>
                  <th scope="col" className="py-1.5 pr-3 text-right font-medium">+ CBS/IBS</th>
                  <th scope="col" className="py-1.5 pr-3 text-right font-medium">Total da NF</th>
                  <th scope="col" className="py-1.5 pr-3 text-right font-medium">% tributos sobre o total da NF</th>
                  {comPraticado && (
                    <>
                      <th scope="col" className="py-1.5 pr-3 text-right font-medium">% tributos sobre o total da NF (praticado)</th>
                      <th scope="col" className="py-1.5 pr-3 text-right font-medium">Lucro simulado</th>
                      <th scope="col" className="py-1.5 pr-3 text-right font-medium">Lucro (R$)</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {consulta.data.etapas.map((e) => (
                  <LinhaEtapa key={`${e.etapa}-${e.data}`} etapa={e} colunas={colunas} comPraticado={comPraticado} onSelecionar={onSelecionar} selecionada={selecionada === `${e.etapa}-${e.data}`} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );

  const conteudo = (
    <>
      {cabecalho}
      {seletor}
      {corpo}
    </>
  );

  if (embutido) {
    return (
      <div data-testid="por-regime" className="flex flex-col gap-3 border-t border-border pt-4">
        {conteudo}
      </div>
    );
  }

  return (
    <section aria-label="Preço por regime" data-testid="por-regime" className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
      {conteudo}
    </section>
  );
}

function LinhaEtapa({
  etapa,
  colunas,
  comPraticado,
  onSelecionar,
  selecionada,
}: {
  etapa: PorRegimeEtapaResponse;
  colunas: number;
  comPraticado: boolean;
  onSelecionar?: (etapa: PorRegimeEtapaResponse) => void;
  selecionada: boolean;
}) {
  return (
    <>
      <tr
        data-testid="por-regime-linha"
        data-atual={etapa.atual ? 'true' : 'false'}
        data-carregada={selecionada ? 'true' : undefined}
        onClick={onSelecionar ? () => onSelecionar(etapa) : undefined}
        className={cn('border-t border-border/60', etapa.atual && 'bg-line-soft font-semibold', onSelecionar && 'cursor-pointer hover:bg-line-soft', selecionada && 'bg-primary/10 outline outline-1 outline-primary/40')}
      >
        <td className="py-1.5 pr-3">
          {onSelecionar ? (
            <button type="button" aria-pressed={selecionada} title="Carregar a configuração deste ano no topo" className="rounded-md text-left outline-none focus-visible:shadow-focus">
              {etapa.etapa}
            </button>
          ) : (
            etapa.etapa
          )}
          {etapa.atual && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-caption font-medium text-primary">atual</span>}
        </td>
        <td className="py-1.5 pr-3">
          {REGIME_ROTULO[etapa.regime]}
          {etapa.aliquotaEstimada && (
            <span className="mt-1 block">
              <SeloEstimada />
            </span>
          )}
        </td>
        <td className="py-1.5 pr-3">{formatarData(etapa.data)}</td>
        <td className="tabular py-1.5 pr-3 text-right">{formatarMoeda(etapa.sugerido.precoSemTributos)}</td>
        <td className="tabular py-1.5 pr-3 text-right">{formatarMoeda(etapa.sugerido.cbsIbs)}</td>
        <td className="tabular py-1.5 pr-3 text-right">{formatarMoeda(etapa.sugerido.totalNf)}</td>
        <td className="tabular py-1.5 pr-3 text-right">
          {formatarFracaoPercentual(etapa.percTributosSobreNf)}
          <span className="block text-caption text-ink-muted">
            Fixa {formatarMoeda(etapa.fixaRs)} · Lucro {formatarMoeda(etapa.lucroRs)}
          </span>
        </td>
        {comPraticado && (
          <>
            <td className="tabular py-1.5 pr-3 text-right">{etapa.percTributosSobreNfPraticado !== null ? formatarFracaoPercentual(etapa.percTributosSobreNfPraticado) : '—'}</td>
            <td className="tabular py-1.5 pr-3 text-right">{etapa.lucroSimulado ? formatarFracaoPercentual(etapa.lucroSimulado) : '—'}</td>
            <td className="tabular py-1.5 pr-3 text-right">{etapa.lucroSimuladoValor ? formatarMoeda(etapa.lucroSimuladoValor) : '—'}</td>
          </>
        )}
      </tr>
      {etapa.erro && (
        <tr className="text-danger-ink">
          <td colSpan={colunas} className="pb-2 text-caption">
            <span role="alert">
              {etapa.erro.message} <span className="text-ink-muted">({etapa.erro.code})</span>
            </span>
          </td>
        </tr>
      )}
    </>
  );
}
